import {EditableProps} from "../../shared/editable/EditableProps";
import {SectionInterface} from "../../../model/SectionInterface";
import {useAppSelector} from "../../../../redux/hook";
import {selectViewState} from "../../../../redux/features/view/viewSlice";
import {doViewGenerator} from "../../../util/UIFactory";
import styles from './ThreeColumns.module.scss';
import {NewType} from "../../../model/NewOption";
import {DeleteType} from "../../../model/DeleteOption";
import EditablePanel from "../../shared/editable/EditablePanel";
import {SettingGroup, SettingGroupType, SettingOption, SettingType} from "../../../model/SettingOption";
import {SettingChangeFunction} from "../../setting/SettingChange";
import {StyleUtil} from "../../setting/StyleUtil";
import React, {useCallback, useEffect, useState} from "react";
import EmptyLabel from "../../shared/emptylabel/EmptyLabel";
import {nanoid} from "nanoid";
import useWindowSize from "../../../hook/useWindowSize";
import useWidgetState from "../../../../hook/useWidgetState";


export default function TreeColumns(props: EditableProps) {
    const windowSize = useWindowSize();
    const sectionData:SectionInterface = props.data as SectionInterface;
    const {sectionId,sectionProps} = sectionData;
    const viewState = useAppSelector(selectViewState);
    const [refreshId,setRefreshId] = useState(nanoid());

    useWidgetState(sectionId,(event) => {
        setRefreshId(nanoid());
    })


    const [views , setViews] = useState<JSX.Element[]|null>(null);

    // const views:JSX.Element[] = doViewGenerator(sectionData.sectionWidgets,
    //     props.uiLevel+1,
    //     props.uiLevelStr,
    //     props.sharedObjects,
    //     props.onHandleSetting,
    //     props.onHandleDeletion,
    //     props.onHandleAddNewSectionOrWidget,
    //     props.onHandleSwap,
    //     props.onHandleUpdate
    // );

    useEffect(() => {
        setViews(doViewGenerator(sectionData.sectionWidgets,
            props.uiLevel+1,
            props.uiLevelStr,
            props.sharedObjects,
            props.onHandleSetting,
            props.onHandleDeletion,
            props.onHandleAddNewSectionOrWidget,
            props.onHandleSwap,
            props.onHandleUpdate,
            props.onHandleClone,
            props.onHandleCopy,
            props.onHandleCut,
            props.onHandleCreateTemplate,
            props.updateWidgetData,
        ));
    },[refreshId])

    if(!(sectionProps['content'])) {
        sectionProps['content'] = {
            'column1-width': "30%",
            'column2-width': "1fr",
            'column3-width': "1fr",
            gap: "0px",
        }
    }

    if(!sectionProps['content']['gap']) {
        sectionProps['content']['gap'] = "0px";
    }

    const gridTemplateColumns = (col1:string,col2:string,col3:string) => {
        if(col1 === col2 && col3) {
            return `repeat(auto-fill,${col1})`
        } else {
            return `${col1} ${col2} ${col3}`;
        }
    }

    const themeName = "three_columns";
    const sectionStyles = {
        theme: StyleUtil.toThemeNameByType(sectionProps, themeName),
        current: StyleUtil.toClassFromWidget(sectionId, sectionProps, "current_style"),
        panel: StyleUtil.toClassFromWidget(sectionId , sectionProps, "bt_threecolumns_panel"),
        flow_panel: StyleUtil.toClassFromMap(sectionId, "flow_panel",{
            "grid-template-columns" : gridTemplateColumns(sectionProps['content']['column1-width'] , sectionProps['content']['column2-width'] , sectionProps['content']['column3-width']),
            "gap" : sectionProps['content']['gap']
        })
    }

    const ensureScreen = (node:any) => {
        let rootElement = node;
        let rootBoundary = rootElement.getBoundingClientRect();
        if(rootBoundary.width !== 0) {
            if (rootBoundary.width < 700) {
                rootElement.style.gridTemplateColumns = '100%'
            } else {
                rootElement.style.gridTemplateColumns = null;
            }
        } else {
            setTimeout(() => {
                ensureScreen(node);
            },100);
        }
    }

    const rootRef = useCallback((node) => {
        if(windowSize && node) {
            ensureScreen(node);
        }
    },[windowSize]);





    const createThreeColumn = (views:JSX.Element[]) => {
        return <section
            key={refreshId}
            ref={rootRef}
            id={"test"}
            className={
                StyleUtil.combineClasses(styles.Root,sectionStyles.theme,sectionStyles.current,sectionStyles.panel,sectionStyles.flow_panel)
            }
        >
            <div className={"col1"}>{views.length >= 1 ? views.filter((view,index) => index % 3 === 0) : <EmptyLabel label={"1st column"}/>}</div>
            <div className={"col2"}>{views.length >= 2 ? views.filter((view,index) => index % 3 === 1) : <EmptyLabel label={"2nd column"}/>}</div>
            <div className={"col3"}>{views.length >= 3 ? views.filter((view,index) => index % 3 === 2) : <EmptyLabel label={"3nd column"}/>}</div>
        </section>
    }

    if(views === null) return <></>
    if(viewState.isEdit) {
        const onChangeSettings: SettingChangeFunction|null = null;
        const suggestTags: string[] = [
            "current",
            "current .col1",
            "current .col2",
            "current .col3",
        ]
        const settingOptions:SettingOption[] = [
            { title: "General", type: SettingType.widget,  editor: "threecolumnssetting" , group: "content", groupType: SettingGroupType.layout},
            { type: SettingType.background, group: "bt_threecolumns_panel" , groupType: SettingGroupType.style},
            { type: SettingType.margin,  group: "bt_threecolumns_panel", groupType: SettingGroupType.advanced},
            { type: SettingType.padding, group: "bt_threecolumns_panel", groupType: SettingGroupType.advanced},
        ];
        const settingGroups:SettingGroup[] = [
            {group: "content", groupName: "General"},
            {group: "bt_threecolumns_panel", groupName: "Panel"}
        ]
        return (
            <EditablePanel {...props}
                           themeName={themeName}
                           newUILevelStr={props.uiLevelStr + "_" + (views.length)}
                           newOption={{ type: NewType.widget_or_section, tag: null}}
                           onHandleAddNewSectionOrWidget={props.onHandleAddNewSectionOrWidget}
                           suggestTags={suggestTags}
                           settingOptions={settingOptions}
                           settingGroups={settingGroups}
                           onChangeSettings={onChangeSettings}
                           deleteOption={{type: DeleteType.delete_with_rearrange, tag: null}}
                           >
                {createThreeColumn(views)}
            </EditablePanel>
        )
    } else {
        return createThreeColumn(views);
    }
}